<template>
  <div id="app-container">
    <LydiaShortageModal
      v-model="showLydiaShortageModal"
      @confirm="handleModalConfirm"
      @charge="handleModalCharge"
    />
    <div id="title">내가 보는 주역점</div>
    <div id="subtitle">
      마음의 흐름이 작용하는 여섯 번의 순간을
      정통 주역점 절차로 치환하여 괘를 뽑습니다.
    </div>
    <!-- 사용자 질문 입력 필드 추가 -->
    <div id="question-input-area">
      <textarea
        v-model="userQuestion"
        placeholder="하늘에 물을 내용을 여기 적어주세요(80자 이내). 그냥 마음에 묻어두고 점을 치셔도 상관 없습니다."
        maxlength="80"
      ></textarea>
    </div>
    <!-- 여기까지 사용자 질문 입력 필드 추가 -->

    <div id="hexagram-area">
      <div class="hexagrams">
        <div class="hex" id="mainHex">
          <img
            v-for="(eff, i) in [...effs].reverse()"
            :key="eff"
            class="eff"
            :src="effImage(eff)"
            :alt="eff"
          />
        </div>
        <div class="hex" v-if="effs.length === 6 && hasChangedEffs">
          <img
            v-for="(eff, i) in [...changedEffs].reverse()"
            :key="'c' + i"
            class="eff"
            :src="effImage(eff)"
            :alt="eff"
          />
        </div>
      </div>
    </div>
    <div id="hexagram-names">{{ hexagramName }}</div>
    <div id="tabs" @click="handleTap">{{ tabGuide }}</div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { trigramMap, hexagramNames } from '@/data/hexagramMap'
  import { useStore } from 'vuex'
  import { canUseThis } from '@/utils/checkLydia'
  import LydiaShortageModal from '@/components/LydiaShortageModal.vue'

  const store = useStore()
  const router = useRouter()

  const effs = ref([])
  const changedEffs = ref([])
  const hexagramName = ref('')
  const tabGuide = ref('진심을 담아 화면을 탭 하세요')
  const isLocked = ref(false)
  const isInterpretationReady = ref(false)

  const currentHexKey = ref('')
  const currentChangedHexKey = ref('')

  const userQuestion = ref('')

  const effLabels = ['초효', '2효', '3효', '4효', '5효', '상효']

  const effImage = (eff) => {
    // console.log('eff', eff);
    const map = {
      소양: 'images/yang.png',
      소음: 'images/yin.png',
      노양: 'images/yang-changed.png',
      노음: 'images/yin-changed.png',
    }
    return map[eff] || 'images/yin.png'
  }

  function generateEffFromSeed(seed) {
    const rand = (s) => Math.floor((Math.sin(s++) * 10000) % 100)
    const nums = [rand(seed), rand(seed + 1), rand(seed + 2)].map(
      (n) => Math.abs(n % 100) + 1,
    )
    const oddCount = nums.filter((n) => n % 2 === 1).length
    if (oddCount === 3) return '노양'
    if (oddCount === 0) return '노음'
    if (oddCount === 1) return '소양'
    return '소음'
  }

  const count = ref(0)
  const extractCharName = (name) => name?.split('(')[0] || '알 수 없음'

  const hasChangedEffs = computed(() =>
    effs.value.some((e) => e === '노양' || e === '노음'),
  )

  // 진동 트리거
  if (navigator.vibrate) navigator.vibrate(1000)

  // Lydia 부족 팝업 상태
  const showLydiaShortageModal = ref(false)
  // 모달 확인 버튼 클릭 핸들러
  const handleModalConfirm = () => {
    router.go(-1) // '확인' 클릭 시 IChingDaily로 이동
  }
  // 모달 충전 버튼 클릭 핸들러
  const handleModalCharge = () => {
    router.replace('/ChargeLydia') // '충전' 클릭 시 ChargeLydia로 이동 (가정)
  }

  // onMounted 훅에서 canUseThis 호출 및 로직 분기
  onMounted(async () => {
    const canProceed = await canUseThis('divination', store) // canUseThis 호출

    if (canProceed) {
      // canUseThis가 true를 반환하면 기존 기능 진행
      console.log('Service available. Proceeding with Divination.')
    } else {
      // canUseThis가 false를 반환하면 팝업 표시 및 라우팅
      showLydiaShortageModal.value = true
      console.log('Service unavailable. Showing Lydia Shortage Modal.')
    }
  })

  const handleTap = () => {
    if (effs.value.length >= 6) {
      if (isInterpretationReady.value) {
        console.log(
          'main:',
          currentHexKey.value,
          'changed:',
          currentChangedHexKey.value,
          'effs:',
          effs.value.join(','),
          'question:',
          userQuestion.value,
          'birth:',
          store.getters.getUser.birthDate,
        )
        router.replace({
          path: './Interpretation',
          query: {
            main: currentHexKey.value,
            changed: currentChangedHexKey.value,
            changedEffs: effs.value.join(','),
            question: userQuestion.value,
            birth: store.getters.getUser.birthDate,
          },
        })
        return
      }
      return
    }
    isLocked.value = true
    if (navigator.vibrate) navigator.vibrate(600)

    tabGuide.value = `${effLabels[effs.value.length]}를 생성하고 있습니다`

    const seed = performance.now()
    const eff = generateEffFromSeed(seed)

    const generateEffs = (eff) => {
      if (count.value > 0) {
        effs.value.splice(0, effs.value.length)
      }
      const newId = Date.now()
      effs.value.push({ id: newId, text: eff })
      count.value++
    }

    setTimeout(() => {
      effs.value.push(eff)
      if (effs.value.length < 6) {
        tabGuide.value = '진심을 담아 화면을 탭 하세요'
        isLocked.value = false
      } else {
        const lower = effs.value
          .slice(0, 3)
          .map((e) => (e.includes('양') ? '양' : '음'))
          .join('')
        const upper = effs.value
          .slice(3, 6)
          .map((e) => (e.includes('양') ? '양' : '음'))
          .join('')
        const hexKey = trigramMap[upper] + trigramMap[lower]
        currentHexKey.value = hexKey
        const name = hexagramNames[hexKey] || '알 수 없음'

        const changed = effs.value.map((e) => {
          if (e === '노양') return '소음'
          if (e === '노음') return '소양'
          return e
        })
        changedEffs.value = changed.slice()

        const lower2 = changed
          .slice(0, 3)
          .map((e) => (e.includes('양') ? '양' : '음'))
          .join('')
        const upper2 = changed
          .slice(3, 6)
          .map((e) => (e.includes('양') ? '양' : '음'))
          .join('')
        const hexKey2 = trigramMap[upper2] + trigramMap[lower2]
        currentChangedHexKey.value = hexKey2
        const name2 = hexagramNames[hexKey2] || ''

        hexagramName.value =
          name + (name2 && name !== name2 ? ` → ${name2}` : '')

        const mainChar = extractCharName(name)
        const changedChar = extractCharName(name2)

        tabGuide.value =
          name2 && name !== name2
            ? `${mainChar}(지괘 ${changedChar})을(를) 얻으셨습니다.\n가벼운 탭으로 풀이를 확인하세요.`
            : `${mainChar}을(를) 얻으셨습니다.\n가벼운 탭으로 풀이를 확인하세요.`

        isInterpretationReady.value = true
      }
    }, 1000) // for dev, for publish 2000
  }
</script>

<style scoped>
  #app-container {
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    text-align: center;
    max-width: 480px; /* 모바일 화면 크기 고려*/
    width: var(--device-width);
    margin: auto; /* 가운데 정렬 */
    background-color: white;
  }
  #title {
    font-size: var(--font-size-15); /* 폰트 크기 조정 */
    margin-bottom: 1.5rem;
    font-weight: bold;
    align-self: center; /* 타이틀만 가운데 정렬 */
  }

  #subtitle {
    padding: 1rem;
    text-align: center;
    font-size: var(--font-size-11);
    color: #333;
    background-color: white;
    width: 100%;
  }

  #question-input-area {
    margin-top: 20px; /* 필요에 따라 간격 조정 */
    align-self: center;
    text-align: center;
    width: 100%;
    margin: 0 auto;
  }

  #question-input-area textarea {
    /* input 대신 textarea 선택 */
    width: 100%; /* 필요에 따라 너비 조정 (탭 영역에 맞게) */
    height: 120px; /* 두 줄 표현될 수 있도록 높이 조정 */
    padding: 10px;
    font-size: var(--font-size-10);
    border: 1px solid #ccc;
    border-radius: 5px;
    resize: none; /* textarea 크기 조절 비활성화 */
    box-sizing: border-box; /* padding과 border가 width와 height에 포함되도록 설정 */
    white-space: pre-wrap; /* 자동 줄바꿈 */
    word-wrap: break-word; /* 단어 단위 줄바꿈 */
  }

  #hexagram-area {
    height: 40%;
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: end;
    padding: 0.5rem;
  }
  .hexagrams {
    display: flex;
    gap: 1rem;
  }
  .hex {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding-top: 1rem;
    height: 180px;
  }
  .eff {
    width: 120px;
    height: 20px;
    margin: 2px;
  }
  #hexagram-names {
    height: 10%;
    text-align: center;
    font-size: var(--font-size-13);
    padding-bottom: 0.25rem;
  }
  #tabs {
    height: 35%;
    width: 100%;
    background: linear-gradient(to top, #111, #222);
    color: white;
    font-style: italic;
    text-align: center;
    padding: 1rem;
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    font-size: var(--font-size-11);
    animation: waveBackground 6s infinite ease-in-out;
    white-space: pre-line;
  }
  @keyframes waveBackground {
    0% {
      background: linear-gradient(135deg, #111, #222);
    }
    25% {
      background: linear-gradient(135deg, #222, #333);
    }
    50% {
      background: linear-gradient(135deg, #111, #222);
    }
    75% {
      background: linear-gradient(135deg, #222, #444);
    }
    100% {
      background: linear-gradient(135deg, #111, #222);
    }
  }
</style>
